// ========================================
// Ant Design 5 Theme Configuration
// ========================================
// This file contains the complete theme configuration for Ant Design 5.x
// using the token-based theming system (ConfigProvider theme prop).
//
// References:
// - SCSS Variables: src/assets/styles/abstracts/_variables.scss
// - Migration Doc: ANTD_TOKEN_MIGRATION.md
// ========================================

const antdTheme = {
	token: {
		// =========================
		// Primary branding colors
		// =========================
		colorPrimary: "#01a2b0", // main primary color
		colorPrimaryHover: "#20b5bd", // primary color on hover
		colorPrimaryActive: "#20b5bd", // primary color when active/pressed
		colorPrimaryBorder: "#01a2b0", // optional: primary border color

		// =========================
		// Success / Positive feedback
		// =========================
		colorSuccess: "#52c41a", // success main color
		colorSuccessHover: "#73d13d", // hover state
		colorSuccessActive: "#389e0d", // active state

		// =========================
		// Warning / Caution
		// =========================
		colorWarning: "#faad14",
		colorWarningHover: "#ffc53d",
		colorWarningActive: "#d48806",

		// =========================
		// Error / Negative feedback
		// =========================
		colorError: "#ff4d4f",
		colorErrorHover: "#ff7875",
		colorErrorActive: "#d9363e",

		// =========================
		// Info hover / active states (optional)
		// =========================
		colorInfo: "#01a2b0",
		colorInfoHover: "#33b5c6",
		colorInfoActive: "#008293",

		// Link colors
		colorLink: "#01a2b0",
		colorLinkHover: "#029dab",
		colorLinkActive: "#007a8a", // $primary-dark-color

		// Text colors
		colorText: "rgb(34, 34, 34)", // $text-color
		colorTextSecondary: "#979797", // $text-mid-2-dark-color
		colorTextTertiary: "#afafaf", // $text-dark-color
		colorTextQuaternary: "#d9d9d9", // $text-mid-dark-color

		// Background colors
		colorBgContainer: "#fff", // $form-bg-color
		colorBgElevated: "#fff",
		colorBgLayout: "#FAFAFD", // Layout background
		colorBgSpotlight: "rgba(0,0,0,0.85)",

		// Border colors
		colorBorder: "#d9d9d9",
		colorBorderSecondary: "#eee", // $form-inner_block-border-color

		// ========================================
		// Typography
		// ========================================
		fontFamily: "'Rubik', 'Helvetica Neue Light', 'Helvetica Neue', 'Helvetica', 'Arial', sans-serif",
		fontSize: 14,
		fontSizeHeading1: 38,
		fontSizeHeading2: 30,
		fontSizeHeading3: 24,
		fontSizeHeading4: 20,
		fontSizeHeading5: 16,

		// ========================================
		// Layout & Spacing
		// ========================================
		borderRadius: 4,
		borderRadiusLG: 4,
		borderRadiusSM: 4,

		// Shadows (from $box-shadow)
		boxShadow: "0 2px 18px rgba(0, 0, 0, 0.06)",
		boxShadowSecondary: "0 2px 8px rgba(0, 0, 0, 0.15)",

		// Control heights
		controlHeight: 32,
		controlHeightLG: 40,
		controlHeightSM: 24,

		// ========================================
		// Interaction States
		// ========================================
		// (colorPrimaryHover, colorPrimaryActive, colorPrimaryBorder already defined above)

		// Focus
		colorPrimaryBg: "rgba(1, 162, 176, 0.1)",
		controlOutline: "rgba(1, 162, 176, 0.2)",

		// ========================================
		// Component-specific tokens
		// ========================================
		// Switch
		colorPrimaryBorderHover: "#029dab",
	},
	components: {
		// ========================================
		// Button Component
		// ========================================
		Button: {
			primaryColor: "#ffffff", // Text color on primary button
			colorPrimary: "#01a2b0",
			colorPrimaryHover: "#029dab",
			colorPrimaryActive: "#007a8a",
			defaultBorderColor: "#d9d9d9",
			defaultColor: "rgb(34, 34, 34)",
			borderRadius: 4,
			controlHeight: 32,
			controlHeightLG: 40,
			controlHeightSM: 24,
		},

		// ========================================
		// Input Component
		// ========================================
		Input: {
			colorText: "#000", // Explicit black text
			colorBorder: "#d9d9d9",
			colorPrimaryHover: "#01a2b0",
			activeBorderColor: "#01a2b0",
			activeShadow: "0 0 0 2px rgba(1, 162, 176, 0.2)",
			hoverBorderColor: "#01a2b0",
			borderRadius: 4,
		},

		// ========================================
		// Select Component
		// ========================================
		Select: {
			colorBorder: "#d9d9d9",
			colorPrimaryHover: "#01a2b0",
			controlOutline: "rgba(1, 162, 176, 0.2)",
			borderRadius: 4,
		},

		// ========================================
		// Checkbox Component
		// ========================================
		Checkbox: {
			colorPrimary: "#01a2b0",
			colorPrimaryHover: "#029dab",
			colorPrimaryBorder: "#01a2b0",
		},

		// ========================================
		// Radio Component
		// ========================================
		Radio: {
			colorPrimary: "#01a2b0",
			colorPrimaryHover: "#01a2b0",
			colorPrimaryBorder: "#01a2b0",
			buttonSolidCheckedColor: "#ffffff",
		},

		// ========================================
		// Switch Component
		// ========================================
		Switch: {
			colorPrimary: "#01a2b0",
			colorPrimaryHover: "#029dab",
		},

		// ========================================
		// Tabs Component
		// ========================================
		Tabs: {
			colorPrimary: "#01a2b0",
			inkBarColor: "#01a2b0",
			itemActiveColor: "#01a2b0",
			itemHoverColor: "#01a2b0",
			itemSelectedColor: "#01a2b0",
		},

		// ========================================
		// Pagination Component
		// ========================================
		Pagination: {
			colorPrimary: "#01a2b0",
			colorPrimaryHover: "#01a2b0",
			itemActiveBg: "#ffffff",
			itemActiveBgDisabled: "#f5f5f5",
		},

		// ========================================
		// Menu Component
		// ========================================
		Menu: {
			colorPrimary: "#01a2b0",
			itemSelectedColor: "#01a2b0",
			itemHoverColor: "#01a2b0",
			itemActiveBg: "transparent",
			itemSelectedBg: "transparent",
			subMenuItemBg: "#fff",
		},

		// ========================================
		// Slider Component
		// ========================================
		Slider: {
			trackBg: "#01a2b0",
			trackHoverBg: "#029dab",
			handleColor: "#01a2b0",
			handleActiveColor: "#01a2b0",
			dotBorderColor: "#01a2b0",
			handleActiveOutlineColor: "rgba(1, 162, 176, 0.2)",
		},

		// ========================================
		// Form Component
		// ========================================
		Form: {
			labelColor: "#979797", // Grey labels
			verticalLabelPadding: "0 0 8px",
			itemMarginBottom: 24,
		},

		// ========================================
		// Modal Component
		// ========================================
		Modal: {
			borderRadius: 4,
			contentBg: "#fff",
			headerBg: "#fff",
		},

		// ========================================
		// Notification Component
		// ========================================
		Notification: {
			zIndexPopup: 9999999, // From _shame.scss
		},

		// ========================================
		// Table Component
		// ========================================
		Table: {
			fontFamily: "'Rubik', 'Helvetica Neue Light', 'Helvetica Neue', 'Helvetica', 'Arial', sans-serif",
			borderRadius: 4,
		},

		// ========================================
		// Dropdown Component
		// ========================================
		Dropdown: {
			borderRadius: 4,
		},

		// ========================================
		// Layout Component
		// ========================================
		Layout: {
			bodyBg: "#FAFAFD", // Content background
			headerBg: "#fff",
			footerBg: "#fff",
			siderBg: "#fff",
		},
	},
};

export default antdTheme;
